import { useEffect, useState } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import { useLoader } from '../../../../provider/LoaderProvider';

import useParam from '../../../../context/ParamContext';
import useUsers from '../../../../context/UsersContext';
import { KanbanType, KanbanFilterFormState, conditions } from '../../../../config/kanban';
import { generateAutocompleteItems, generateFilter } from '../../../../utils/helper';
import { ColorPicker } from '../../../../config/ColorPicker';
import Card from './Card';
import MessageDialog from './MessageDialog';
import masterDataService from '../../../../service/masterData.service';
import { Chip, Grid, IconButton, Slider } from '@mui/material';
import StatusDialog from '../../../Status/StatusDialog';
import kanbanService from '../../../../service/kanban.service';
import { AutoCompleteSelectUI, DebouncedInputUI, FormLabelUI } from '../../../Interface';
import SimpleTaskDialog from './SimpleTaskDialog';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CardMenu from './CardMenu';
import simpletaskService from '../../../../service/simpletask.service';

const FpKanbanView = () => {
  const { getUser, user, getRole } = useUsers();
  const { getParam, params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [kanbanData, setKanbanData] = useState([]);
  const [dialogOpen, setDialogOpen] = useState({ open: false, data: {} });
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [filterFormState, setFilterFormState] = useState({});
  const [loading, setLoading] = useState(null);
  const [defaultFilterFields, setDefaultFilterFields] = useState([]);
  const [taskChanged, setTaskChanged] = useState(false);
  const [messageData, setMessageData] = useState({
    addressedTo: null,
    eventData: {},
  });
  const [status, setStatus] = useState({});
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    companyLocation: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [refetch, setRefetch] = useState(false);
  const [roles, setRoles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const defSlideCount = 3;
  const maxSlideCount = 3;
  const [slideCount, setSlideCount] = useState(defSlideCount);
  const [collapsedCols, setCollapsedCols] = useState([]);
  const [menuPosition, setMenuPosition] = useState(null);
  const [cardMenuData, setCardMenuData] = useState({});

  const [query, setQuery] = useState({
    filter: [],
  });

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getParam();
  }, []);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const roles = getRole(['TASK_KANBAN_SEE_ONLY_OWN']);

      setRoles(roles);
      initDefaultValues(roles);
    }
  }, [user.userId]);

  useEffect(() => {
    if (taskChanged) {
      getItemsByType();
      setTaskChanged(false);
    }
  }, [taskChanged]);

  const kanbanViewAccess =
    params.KANBAN_VIEW_ACCESS && params.KANBAN_VIEW_ACCESS.split(',').map((access) => parseInt(access));

  const hasAccess = kanbanViewAccess && kanbanViewAccess.includes(user.userId) ? 'Y' : 'N';

  useEffect(() => {
    if (isLoading) return;

    const base = KanbanFilterFormState[KanbanType.TASK] ?? {};
    const next = { ...base };

    const companyLocationId = user?.companyLocation?.id;

    if (roles.TASK_KANBAN_SEE_ONLY_OWN) {
      next.resposiblePersonId = user.userId;
    }

    if (companyLocationId) {
      next.companyLocationId = companyLocationId;
    }

    setFilterFormState(next);
  }, [user.userId, roles, isLoading]);

  useEffect(() => {
    const defaultFilter = [
      {
        field: 'deletedBy',
        value: null,
      },
    ];

    const currentConditions = conditions[KanbanType.TASK];
    const newFilter = generateFilter(filterFormState, currentConditions);
    setQuery((prev) => {
      return { ...prev, filter: [...defaultFilter, ...newFilter] };
    });
    setDefaultFilterFields(defaultFilter);
  }, [filterFormState]);

  useEffect(() => {
    if (refetch) {
      getItemsByType();
    }
  }, [refetch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (defaultFilterFields.length > 0 && query.filter.length > 0 && !isLoading) {
        getItemsByType();
      }
    }, 100);

    return () => clearTimeout(delayDebounceFn);
  }, [query, defaultFilterFields.length, isLoading]);

  const refreshKanbanBoard = params.KANBAN_BOARD_TIMED_UPDATE && Number(params.KANBAN_BOARD_TIMED_UPDATE);

  useEffect(() => {
    if (query && hasAccess && refreshKanbanBoard && typeof refreshKanbanBoard === 'number' && refreshKanbanBoard > 0) {
      const intervalId = setInterval(getItemsByType, refreshKanbanBoard);

      return () => clearInterval(intervalId);
    }
  }, [query, hasAccess]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyLocationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          companyLocation: resp[1],
        };
        setAutoCompleteData(aData);

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          companyLocation: generateAutocompleteItems(aData.companyLocation, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        setIsLoading(false);
        //hideLoader();
      });
  };

  const getStatusByType = () => {
    setStatusDialogOpen(true);
    showLoader();
    kanbanService
      .getStatusByType(KanbanType.TASK)
      .then((data) => {
        setStatus(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getItemsByType = () => {
    showLoader();
    kanbanService
      .getItemsByType(KanbanType.TASK, query, hasAccess)
      .then((data) => {
        setKanbanData(data);
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getItemById = () => {
    showLoader();
    setDialogOpen({ open: true });
    kanbanService
      .getItemById(KanbanType.TASK, cardMenuData.simpleTaskId)
      .then((data) => {
        setDialogOpen((prev) => ({
          ...prev,
          data,
        }));
        handleClose();
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleDeleteTask = () => {
    simpletaskService.deleteSimpleTask(cardMenuData.simpleTaskId).then(() => {
      setKanbanData((prev) =>
        prev.map((kanbanCol) => ({
          ...kanbanCol,
          items: kanbanCol.items.filter((item) => item.simpleTaskId !== cardMenuData.simpleTaskId),
        }))
      );
      handleClose();
    });
  };

  const addOrRemoveRespPerson = (userId) => {
    simpletaskService.addOrRemoveRespPerson(cardMenuData.simpleTaskId, userId).then((data) => {
      setKanbanData((prev) =>
        prev.map((kanbanCol) => ({
          ...kanbanCol,
          items: kanbanCol.items.map((item) =>
            item.simpleTaskId === cardMenuData.simpleTaskId
              ? {
                  ...item,
                  simpleTaskRespPersonlist: data.respPersonlist,
                  messages: data.messages,
                }
              : item
          ),
        }))
      );
    });
  };

  const updateRespPerson = (userId) => {
    const body = {
      simpleTaskId: cardMenuData.simpleTaskId,
      resposiblePersonId: userId,
    };
    simpletaskService.updateRespPerson(body).then((data) => {
      setKanbanData((prev) =>
        prev.map((kanbanCol) => ({
          ...kanbanCol,
          items: kanbanCol.items.map((item) =>
            item.simpleTaskId === cardMenuData.simpleTaskId
              ? {
                  ...item,
                  responsiblePerson: data.responsiblePerson,
                  resposiblePersonId: data.resposiblePersonId,
                  messages: data.messages,
                }
              : item
          ),
        }))
      );
    });
  };

  const handleDropAndStatusUpdate = (statusId, kanbanItem) => {
    setLoading(kanbanItem.id);
    const newStatus = kanbanData.find((kanbanColumn) => kanbanColumn.itemId === statusId);

    const updatedColumn = {
      ...kanbanItem,
      statusId: statusId,
      status: {
        ...kanbanItem.status,
        itemName: newStatus.itemName,
        color: newStatus.color,
        statusId: newStatus.itemId,
      },
    };

    kanbanService
      .updateItemsByType(KanbanType.TASK, kanbanItem.id, statusId, updatedColumn)
      .then((data) => {
        setKanbanData((prev) => {
          return prev.map((kanbanColumn) => {
            if (kanbanColumn.itemId === data.statusId) {
              return {
                ...kanbanColumn,
                items: [data, ...kanbanColumn.items.filter((item) => item.id !== kanbanItem.id)],
              };
            } else if (kanbanColumn.itemId === kanbanItem.statusId) {
              return {
                ...kanbanColumn,
                items: kanbanColumn.items.filter((item) => item.id !== kanbanItem.id),
              };
            }
            return kanbanColumn;
          });
        });
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const onDragStart = (event, kanbanItem) => {
    event.dataTransfer.setData('kanbanItem', JSON.stringify(kanbanItem));
  };

  const showMessageDialogParam = params.KANBAN_SHOW_MESSAGE_DIALOG ? JSON.parse(params.KANBAN_SHOW_MESSAGE_DIALOG) : [];

  const showMessageDialog =
    showMessageDialogParam && showMessageDialogParam.find((dialogPr) => dialogPr.kanbanType.includes(KanbanType.TASK));

  const onDragOver = (event) => {
    event.preventDefault();
  };

  const onDrop = (event, statusId) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    const kanbanItem = JSON.parse(event.dataTransfer.getData('kanbanItem'));

    if (kanbanItem.statusId !== statusId) {
      handleDropAndStatusUpdate(statusId, kanbanItem);
      if (showMessageDialog) {
        setMessageDialogOpen(true);
        setMessageData((prev) => ({
          ...prev,
          eventData: kanbanItem,
        }));
      }
    }
  };

  const getGridSize = () => {
    switch (slideCount) {
      case 3:
        return 12;
      case 2:
        return 6;
      case 1:
        return 4;
      default:
        return 12;
    }
  };

  const handleCollapseCol = (statusId) => {
    setCollapsedCols((prev) =>
      prev.includes(statusId) ? prev.filter((item) => item !== statusId) : [...prev, statusId]
    );
  };

  const handleClick = (event, data) => {
    setMenuPosition(menuPosition === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
    setCardMenuData(data);
  };

  const handleClose = () => {
    setMenuPosition(null);
    setCardMenuData({});
  };

  const currentTask = kanbanData
    .flatMap((col) => col.items)
    .find((task) => task.simpleTaskId === cardMenuData.simpleTaskId);

  return (
    <div className="bg-white relative pt-1 w-full px-3 pb-3 font-poppins" style={{ height: 'calc(100vh - 65px)' }}>
      <div className="hidden lg:block fixed top-[10px] lg:right-[170px] z-40">
        <IconButton onClick={() => getStatusByType()}>
          <SettingsIcon sx={{ fontSize: '1.8rem' }} />
        </IconButton>
      </div>
      <div className="2xl:left-80 z-0 2xl:z-40 2xl:absolute 2xl:-top-14">
        <div className="flex flex-col lg:flex-row w-full lg:gap-3">
          <div className="lg:w-[200px]">
            <DebouncedInputUI
              label={<FormLabelUI text="Név" textColor="2xl:text-white" />}
              debounceMs={800}
              setQuickFilterSearchValue={(newValue) => {
                setFilterFormState((prevState) => ({
                  ...prevState,
                  simpleTaskName: newValue,
                }));
              }}
              quickFilterSearchValue={filterFormState.simpleTaskName}
            />
          </div>
          <div className="lg:w-[200px]">
            <AutoCompleteSelectUI
              variant="standard"
              fullWidth
              onChange={(_e, newVal, reason) =>
                setFilterFormState((prev) => ({
                  ...prev,
                  resposiblePersonId: newVal?.value,
                }))
              }
              selectedValue={filterFormState.resposiblePersonId}
              selectedLabelValue={
                autoCompleteOptions.user.find((us) => us.value === filterFormState.resposiblePersonId)?.label
              }
              label={<FormLabelUI text="Felelős" textColor="2xl:text-white" />}
              dataset={autoCompleteOptions.user}
              disabled={roles.TASK_KANBAN_SEE_ONLY_OWN}
            />
          </div>
          <div className="lg:w-[200px]">
            <AutoCompleteSelectUI
              variant="standard"
              fullWidth
              onChange={(_e, newVal, reason) =>
                setFilterFormState((prev) => ({
                  ...prev,
                  companyLocationId: newVal?.value,
                }))
              }
              selectedValue={filterFormState.companyLocationId}
              selectedLabelValue={
                autoCompleteOptions.companyLocation.find((us) => us.value === filterFormState.companyLocationId)?.label
              }
              label={<FormLabelUI text="Telephely" textColor="2xl:text-white" />}
              dataset={autoCompleteOptions.companyLocation}
            />
          </div>
          <div className="mt-2 lg:mt-0 w-[100px] 2xl:text-white">
            <p className="text-center">Zoom</p>
            <Slider
              defaultValue={defSlideCount}
              step={1}
              min={1}
              max={maxSlideCount}
              sx={{
                '@media (min-width:1320px)': {
                  color: 'white',
                },
              }}
              onChange={(e) => setSlideCount(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto custom-scrollbar">
        <div className="w-full mt-4 2xl:pt-0 flex gap-[12px]">
          {kanbanData.map((kanban) => {
            const statusColor = ColorPicker.find((col) => col.colorName === kanban.color);
            const secondaryColor = statusColor ?? kanban?.color;
            const isCollapsed = collapsedCols.includes(kanban.itemId);
            return (
              <div
                onClick={isCollapsed ? () => handleCollapseCol(kanban.itemId) : undefined}
                key={kanban.itemId}
                className="relative bg-[#FaFaFa] text-[#1A1A1A] transition-all"
                style={{
                  minWidth: isCollapsed ? '50px' : '280px',
                  flex: isCollapsed ? '0 0 50px' : '1 1 280px',
                  borderRadius: '8px',
                  borderTop: `7px solid ${statusColor ? statusColor?.colorCode : secondaryColor}`,
                }}
              >
                <div
                  onDragOver={(event) => onDragOver(event)}
                  onDrop={(event) => onDrop(event, kanban.itemId)}
                  style={{
                    minWidth: 'auto',
                  }}
                >
                  <div className={`flex items-center justify-between ${isCollapsed && 'flex-col-reverse'}`}>
                    <div
                      className={`p-[14px] text-[16px] relative flex justify-between items-center font-semibold w-[100%] ${isCollapsed && 'whitespace-nowrap rotate-90 gap-3'}`}
                    >
                      <p>{kanban.itemName}</p>
                      <div>
                        <Chip
                          label={kanban.items.length}
                          sx={{
                            background: '#f2f2f2',
                            color: '#808080',
                          }}
                        />
                      </div>
                    </div>
                    <IconButton
                      sx={{ p: 0.5, mt: isCollapsed && 2 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCollapseCol(kanban.itemId);
                      }}
                    >
                      {isCollapsed ? (
                        <KeyboardArrowRightIcon sx={{ fontSize: '1.7rem' }} />
                      ) : (
                        <KeyboardArrowLeftIcon sx={{ fontSize: '1.7rem' }} />
                      )}
                    </IconButton>
                  </div>
                  <div className="overflow-y-auto custom-scrollbar w-full pb-10 px-3 h-[calc(100vh-320px)] md:h-[calc(100vh-320px)] lg:h-[calc(100vh-240px)] 2xl:h-[calc(100vh-170px)]">
                    <Grid container spacing={1}>
                      {kanban.items.map((data) => {
                        return (
                          <Grid
                            item
                            xs={getGridSize()}
                            width="100%"
                            key={data.id}
                            display={isCollapsed ? 'none' : 'block'}
                          >
                            <Card
                              data={data}
                              onDragStart={onDragStart}
                              statusColor={statusColor}
                              secondaryColor={secondaryColor}
                              loading={loading === data.id ? loading : null}
                              setRefetch={setRefetch}
                              handleClick={handleClick}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <SimpleTaskDialog
        dialogOpen={dialogOpen}
        handleClose={() => setDialogOpen({ open: false, data: {} })}
        changed={{ taskChanged, setTaskChanged }}
      />

      <MessageDialog
        open={messageDialogOpen}
        handleClose={() => {
          setMessageDialogOpen(false);
        }}
        params={params}
        message={{ messageData, setMessageData }}
        user={user}
        setRefetch={setRefetch}
      />

      <StatusDialog
        open={statusDialogOpen}
        handleClose={() => setStatusDialogOpen(false)}
        data={status}
        setDataChanged={setTaskChanged}
      />

      <CardMenu
        handleClose={handleClose}
        open={Boolean(menuPosition)}
        menuPosition={menuPosition}
        getItemById={getItemById}
        handleDeleteTask={handleDeleteTask}
        autoCompleteData={autoCompleteData}
        addOrRemoveRespPerson={addOrRemoveRespPerson}
        currentTask={currentTask}
        updateRespPerson={updateRespPerson}
      />
    </div>
  );
};

export default FpKanbanView;
